import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Descriptions, Tag, Image, Card, Row, Col, Divider, Avatar, Empty, Collapse } from 'antd';
import { 
  UserOutlined, 
  TrophyOutlined, 
  MedicineBoxOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  TeamOutlined,
  StarOutlined
} from '@ant-design/icons';
import { fetchSpecialities } from '../../actions/DepartmentManagementAction';

const { Panel } = Collapse;

const ViewDepartmentModal = ({ visible, onCancel, department }) => {
  const dispatch = useDispatch();
  const { specialities } = useSelector(state => state.departmentManagement);
  const [specialitiesLoading, setSpecialitiesLoading] = useState(false);

  // Fetch specialities when modal opens
  useEffect(() => {
    if (visible && specialities.length === 0) {
      setSpecialitiesLoading(true);
      dispatch(fetchSpecialities())
        .then(() => {
          setSpecialitiesLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching specialities:", error);
          setSpecialitiesLoading(false);
        });
    }
  }, [visible, dispatch, specialities.length]);

  if (!department) return null;

  // Debug: Log department data to see available properties
  console.log("Department data:", department);

  const getStatusTag = (status) => {
    return status === 'Active' ? (
      <Tag color="green" icon={<CheckCircleOutlined />}>
        Active
      </Tag>
    ) : (
      <Tag color="red" icon={<CloseCircleOutlined />}>
        Inactive
      </Tag>
    );
  };

  const getSpecialityName = (specialityId) => {
    const speciality = specialities.find(s => s.specialityId === specialityId);
    return speciality ? speciality.specialityName : specialityId;
  };

  const renderHtmlContent = (htmlContent) => {
    if (!htmlContent) return '-';
    
    return (
      <div 
        className="html-content"
        dangerouslySetInnerHTML={{ __html: htmlContent }}
        style={{
          maxHeight: '200px',
          overflowY: 'auto',
          border: '1px solid #f0f0f0',
          borderRadius: '6px',
          padding: '12px',
          backgroundColor: '#fafafa'
        }}
      />
    );
  };

  // Check if department has doctors data
  const hasDoctors = department.doctorslist && Array.isArray(department.doctorslist) && department.doctorslist.length > 0;

  return (
    <Modal
      title="Department Details"
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={1200}
      className="view-department-modal"
      style={{ top: 20 }}
    >
      <div className="department-view">
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <Card className="department-icon-card">
              {department.ssm_icon ? (
                <Image
                  src={department.ssm_icon}
                  alt={department.ssm_secondary_speciality_name}
                  width={120}
                  height={120}
                  fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
                />
              ) : (
                <div className="no-icon">
                  <UserOutlined style={{ fontSize: '60px', color: '#ccc' }} />
                  <p>No Icon</p>
                </div>
              )}
            </Card>
          </Col>
          
          <Col xs={24} md={16}>
            <Card className="department-info-card">
              <h2>{department.ssm_secondary_speciality_name}</h2>
              {department.ssm_heading && (
                <p className="department-heading">{department.ssm_heading}</p>
              )}
              <div className="department-status">
                Status: {getStatusTag(department.ssm_status)}
              </div>
            </Card>
          </Col>
        </Row>

        <Divider />

        <Descriptions 
          title="Department Information" 
          bordered 
          column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}
          className="department-descriptions"
        >
          <Descriptions.Item 
            label="Department ID" 
            span={1}
          >
            {department.ssm_id}
          </Descriptions.Item>
          
          <Descriptions.Item 
            label="Speciality" 
            span={1}
          >
            {specialitiesLoading ? (
              <span>Loading...</span>
            ) : (
              getSpecialityName(department.specialityName)
            )}
          </Descriptions.Item>
          
          {department.ssm_details && (
            <Descriptions.Item 
              label="Details" 
              span={2}
            >
              <div className="description-content">
                <FileTextOutlined style={{ marginRight: '8px', marginTop: '4px' }} />
                {renderHtmlContent(department.ssm_details)}
              </div>
            </Descriptions.Item>
          )}
          
          {department.ssm_summery && (
            <Descriptions.Item 
              label="Summary" 
              span={2}
            >
              <div className="description-content">
                <FileTextOutlined style={{ marginRight: '8px' }} />
                {department.ssm_summery}
              </div>
            </Descriptions.Item>
          )}
          
          {department.ssm_services && (
            <Descriptions.Item 
              label="Services Offered" 
              span={2}
            >
              <div className="description-content">
                <MedicineBoxOutlined style={{ marginRight: '8px' }} />
                {department.ssm_services}
              </div>
            </Descriptions.Item>
          )}
          
          {department.ssm_awards && (
            <Descriptions.Item 
              label="Awards & Recognition" 
              span={2}
            >
              <div className="description-content">
                <TrophyOutlined style={{ marginRight: '8px' }} />
                {department.ssm_awards}
              </div>
            </Descriptions.Item>
          )}
          
          {department.ssm_icon && (
            <Descriptions.Item 
              label="Icon URL" 
              span={2}
            >
              <div className="description-content">
                <a href={department.ssm_icon} target="_blank" rel="noopener noreferrer">
                  {department.ssm_icon}
                </a>
              </div>
            </Descriptions.Item>
          )}
        </Descriptions>

        {/* Consultation Types Section */}
        <Divider />
        
        <Card 
          title={
            <span>
              <MedicineBoxOutlined style={{ marginRight: '8px', color: '#406076' }} />
              Consultation Types
            </span>
          }
          className="department-consultation-types-card"
        >
          {department.consultationTypes && department.consultationTypes.length > 0 ? (
            <Row gutter={[16, 16]}>
              {department.consultationTypes.map((consultationType, index) => (
                <Col xs={24} sm={12} md={8} key={index}>
                  <Card 
                    size="small" 
                    className="consultation-type-card"
                    hoverable
                  >
                    <div className="consultation-type-content">
                      <div className="consultation-type-name">
                        <strong>{consultationType.consultationType || consultationType.type}</strong>
                      </div>
                      
                      {/* Domestic Fee */}
                      {consultationType.domesticCost && (
                        <div className="consultation-type-fee">
                          <span className="fee-label">Domestic Fee:</span>
                          <span className="fee-amount">₹{consultationType.domesticCost}</span>
                        </div>
                      )}
                      
                      {/* International Fee */}
                      {consultationType.internationalCost && (
                        <div className="consultation-type-fee">
                          <span className="fee-label">International Fee:</span>
                          <span className="fee-amount international">₹{consultationType.domesticCost}</span>
                        </div>
                      )}
                      
                      
                      
                      {consultationType.duration && (
                        <div className="consultation-type-duration">
                          <span className="duration-label">Duration:</span>
                          <span className="duration-value">{consultationType.duration} mins</span>
                        </div>
                      )}
                      
                      {consultationType.description && (
                        <div className="consultation-type-description">
                          {consultationType.description}
                        </div>
                      )}
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No consultation types available for this department"
              className="consultation-types-empty"
            />
          )}
        </Card>

        {/* Doctors Section */}
        <Divider />
        
        <Card 
          title={
            <span>
              <TeamOutlined style={{ marginRight: '8px', color: '#406076' }} />
              Available Doctors
            </span>
          }
          className="department-doctors-card"
        >
          {hasDoctors ? (
            <Collapse 
              accordion 
              className="doctors-accordion"
              expandIconPosition="end"
            >
              {department.doctorslist.map((doctor, index) => (
                <Panel
                  key={index}
                  header={
                    <div className="doctor-accordion-header">
                      <Avatar 
                        size={40} 
                        src={doctor.profileImage || doctor.doctorImage}
                        icon={<UserOutlined />}
                        className="doctor-avatar"
                        
                      />
                      <div className="doctor-header-info">
                        <div className="doctor-name">
                          <strong>{doctor.doctorName || doctor.name}</strong>
                          {doctor.rating && (
                            <span className="doctor-rating">
                              <StarOutlined style={{ color: '#faad14', marginRight: '4px' }} />
                              {doctor.rating}
                            </span>
                          )}
                        </div>
                        
                      </div>
                    </div>
                  }
                  className="doctor-panel"
                >
                  <div className="doctor-accordion-content">
                    {doctor.professionalSummary && (
                      <div className="doctor-summary">
                        <h4>Professional Summary</h4>
                        <div className="summary-content">
                          {doctor.professionalSummary}
                        </div>
                      </div>
                    )}
                    <div className="doctor-details-grid">
                      {doctor.experience && (
                        <div className="doctor-detail-item">
                          <strong>Experience:</strong> {doctor.experience}
                        </div>
                      )}
                      {doctor.qualification && (
                        <div className="doctor-detail-item">
                          <strong>Qualification:</strong> {doctor.qualification}
                        </div>
                      )}
                      {doctor.consultationFee && (
                        <div className="doctor-detail-item">
                          <strong>Consultation Fee:</strong> ₹{doctor.consultationFee}
                        </div>
                      )}
                    </div>
                  </div>
                </Panel>
              ))}
            </Collapse>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No doctors available in this department"
              className="doctors-empty"
            />
          )}
        </Card>
      </div>
    </Modal>
  );
};

export default ViewDepartmentModal;
